import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  AppBar,
  Toolbar,
  Container,
  CircularProgress,
  Alert,
  Stack,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  useMediaQuery,
  MobileStepper
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import {
  AccountCircle,
  AccountBalance,
  School,
  Work,
  Description,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LockClock
} from "@mui/icons-material";
import logo from "../assets/siera-logo.svg";
import { useErrorModal } from "../hooks/useErrorModal";
import {
  validateBankDetails,
  validateEducationDetails,
  validateEmploymentDetails,
  hasValidationErrors,
  getPersonalInfoValidationSummary
} from "../utils/validation";
import PersonalInformationSection from "./onboarding/PersonalInformationSection";
import BankDetailsSection from "./onboarding/BankDetailsSection";
import EducationSection from "./onboarding/EducationSection";
import EmploymentSection from "./onboarding/EmploymentSection";
import JoiningFormsSection from "./onboarding/JoiningFormsSection";
import apiClient from "../api/apiClient";


const EmployeeOnboardingLanding = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const { showError, ErrorModal } = useErrorModal();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(1);

  const [isVerifying, setIsVerifying] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [token, setToken] = useState(null);

  const [isSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [errors, setErrors] = useState({});
  const [rejectedDocs, setRejectedDocs] = useState([]);

  const [progress, setProgress] = useState({
    personalInfoCompleted: false,
    bankDetailsCompleted: false,
    educationCompleted: false,
    employmentCompleted: false,
    joiningFormsCompleted: false,
    onboardingFormsUploaded: [],
    isFullyCompleted: false
  });


  const [personalDraft, setPersonalDraft] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    fatherName: "",
    motherName: "",
    personalEmail: "",
    alternativePhone: "",
    gender: "",
    dob: "",
    nationality: "Indian",
    religion: "",
    bloodGroup: "",
    maritalStatus: "",
    spouseName: "",
    physicallyChallenged: "No",
    disabilityDetails: "",
    disabilityCertificate: [],
    emergencyContact: {
      relation: "",
      name: "",
      phone: "",
    },
    presentAddress: {
      accommodationType: "",
      houseNo: "",
      floor: "",
      houseName: "",
      street: "",
      landmark: "",
      city: "",
      state: "",
      pincode: "",
      rentalAgreement: [],
    },
    permanentAddress: {
      houseNo: "",
      floor: "",
      houseName: "",
      street: "",
      landmark: "",
      city: "",
      state: "",
      pincode: "",
    },
    sameAsPresent: true,
    aadharNo: "",
    aadharName: "",
    aadharFrontDocument: [],
    aadharBackDocument: [],
    panNo: "",
    panName: "",
    panDocument: [],
    additionalIdDocument: [],
    nominees: [
      { nomineeName: "", contactNo: "", nomineeDob: "", relation: "", sharePercentage: 100.00, nomineeType: "Normal" }
    ],
  });

  const [joiningFormsDraft, setJoiningFormsDraft] = useState({
    expectedJoiningDate: "",
  });

  const [bankDraft, setBankDraft] = useState({
    accountName: "",
    accountNumber: "",
    bankName: "",
    ifscCode: "",
    branchName: "",
    passbookDocument: [],
  });

  const [educationList, setEducationList] = useState([
    {
      qualificationLevel: "",
      degree: "",
      branch: "",
      collegeName: "",
      affiliatedUniversity: "",
      yearOfJoining: "",
      yearOfCompletion: "",
      cgpaPercentage: "",
      state: "",
      attachments: [],
    },
  ]);

  const [employmentList, setEmploymentList] = useState([
    {
      companyName: "",
      department: "",
      role: "",
      ctc: "",
      fromDate: "",
      toDate: "",
      address: "",
      isLastEmployment: false,
      offerLetter: [],
      relievingLetter: [],
      experienceLetter: [],
      payslips: [],
      incrementLetter: [],
    },
  ]);


  useEffect(() => {
    const verifyToken = async () => {
      try {
        const tokenParam = searchParams.get('token');

        if (!tokenParam) {
          setTokenError('No token provided. Please use the link from your email.');
          setIsVerifying(false);
          setIsTokenValid(false);
          return;
        }

        setToken(tokenParam);

        const response = await apiClient.get(
          `/employees/verify-document-token/${tokenParam}`
        );

        if (response.data.success) {
          setIsTokenValid(true);
          setEmployeeData(response.data.data);
          setProgress(response.data.data.onboardingProgress || {
            personalInfoCompleted: false,
            bankDetailsCompleted: false,
            educationCompleted: false,
            employmentCompleted: false,
            isFullyCompleted: false
          });

          const prog = response.data.data.onboardingProgress;


          if (prog) {
            // Initializing tab based on completion status
            if (prog.isFullyCompleted) {
              setActiveTab(0); // Show completion screen
            } else if (!prog.personalInfoCompleted) {
              setActiveTab(1);
            } else if (!prog.bankDetailsCompleted) {
              setActiveTab(2);
            } else if (!prog.educationCompleted) {
              setActiveTab(3);
            } else if (!prog.employmentCompleted) {
              setActiveTab(4);
            } else {
              // Move to Joining Forms (Tab 5) if others are done
              setActiveTab(5);
            }
          }



          const fetchedData = response.data.data;
          const docs = fetchedData.documents || [];

          // Helper to find documents by type and optional reference ID
          const findDocs = (type, refId = null) => {
            return docs
              .filter(d => (d.documentType === type || d.documentType === `Joining Form: ${type}` || d.documentType.startsWith(`Experience: ${type}`) || d.documentType === `Experience: ${type}`) && (refId === null || d.referenceId === refId))
              .map(d => ({ name: d.documentName, size: 0, fromServer: true, url: d.fileUrl }));
          };

          if (fetchedData.personalInfo || fetchedData.identities?.length > 0 || fetchedData.addresses?.length > 0) {
            const personal = fetchedData.personalInfo || {};
            const identity = fetchedData.identities?.[0] || {};

            const rejected = docs.filter(d => d.status === 'Rejected');
            if (identity.aadhaarStatus === 'Rejected') {
              rejected.push({ documentType: 'Aadhar Front', rejectionReason: identity.aadhaarRejectionReason || 'Rejected by Admin' });
              rejected.push({ documentType: 'Aadhar Back', rejectionReason: identity.aadhaarRejectionReason || 'Rejected by Admin' });
            }
            if (identity.panStatus === 'Rejected') {
              rejected.push({ documentType: 'PAN', rejectionReason: identity.panRejectionReason || 'Rejected by Admin' });
            }
            if (identity.additionalIdStatus === 'Rejected') {
              rejected.push({ documentType: identity.additionalIdType || 'Additional ID', rejectionReason: identity.additionalIdRejectionReason || 'Rejected by Admin' });
            }
            if (fetchedData.education) {
              fetchedData.education.forEach((e, i) => {
                if (e.status === 'Rejected') {
                  rejected.push({ documentType: 'Education Document', index: i, rejectionReason: e.rejectionReason || 'Rejected by Admin' });
                }
              });
            }
            if (fetchedData.experience) {
              fetchedData.experience.forEach((e, i) => {
                if (e.status === 'Rejected') {
                  rejected.push({ documentType: 'Experience Documents', index: i, rejectionReason: e.rejectionReason || 'Rejected by Admin' });
                }
              });
            }
            setRejectedDocs(rejected);

            // If onboarding is 'completed' but there are rejected docs, direct them to the appropriate tab
            if (prog.isFullyCompleted && rejected.length > 0) {
              if (rejected.some(d => d.documentType.includes('Aadhar') || d.documentType === 'PAN' || d.documentType === 'Additional ID' || d.documentType === 'Profile Photo')) {
                setActiveTab(1);
              } else if (rejected.some(d => d.documentType === 'Passbook' || d.documentType === 'Cheque')) {
                setActiveTab(2);
              } else if (rejected.some(d => d.documentType === 'Education Document')) {
                setActiveTab(3);
              } else if (rejected.some(d => d.documentType === 'Experience Documents')) {
                setActiveTab(4);
              } else {
                setActiveTab(5);
              }
            }
            const addresses = fetchedData.addresses || [];
            const presentAddr = addresses.find(a => a.addressType === 'present') || {};
            const permanentAddr = addresses.find(a => a.addressType === 'permanent') || {};

            setPersonalDraft(prev => ({
              ...prev,
              firstName: personal.firstName || fetchedData.firstName || "",
              middleName: personal.middleName || "",
              lastName: personal.lastName || fetchedData.lastName || "",
              fatherName: personal.fatherName || "",
              motherName: personal.motherName || "",
              personalEmail: personal.personalEmail || "",
              alternativePhone: personal.alternativePhone || "",
              gender: personal.gender || "",
              dob: personal.dob ? personal.dob.split('T')[0] : "",
              nationality: personal.nationality || "Indian",
              religion: personal.religion || "",
              bloodGroup: personal.bloodGroup || "",
              maritalStatus: personal.maritalStatus || "",
              spouseName: personal.spouseName || "",
              physicallyChallenged: personal.isPhysicallyChallenged ? "Yes" : "No",
              disabilityDetails: personal.physicallyChallengedDetails || "",
              emergencyContact: {
                relation: personal.emergencyContactRelation || "",
                name: personal.emergencyContactName || "",
                phone: personal.emergencyContactPhone || "",
              },
              presentAddress: {
                accommodationType: presentAddr.accommodationType || "",
                houseNo: presentAddr.houseNo || "",
                floor: presentAddr.floorNo || presentAddr.floor || "",
                houseName: presentAddr.houseName || "",
                street: presentAddr.street || "",
                landmark: presentAddr.landmark || "",
                city: presentAddr.city || "",
                state: presentAddr.state || "",
                pincode: presentAddr.pincode || "",
                rentalAgreement: findDocs('Rental Agreement'),
              },
              permanentAddress: {
                houseNo: permanentAddr.houseNo || "",
                floor: permanentAddr.floorNo || permanentAddr.floor || "",
                houseName: permanentAddr.houseName || "",
                street: permanentAddr.street || "",
                landmark: permanentAddr.landmark || "",
                city: permanentAddr.city || "",
                state: permanentAddr.state || "",
                pincode: permanentAddr.pincode || "",
              },
              sameAsPresent: permanentAddr.isSameAsPresent || false,
              aadharNo: identity.aadhaarNumber || "",
              aadharName: identity.aadhaarName || "",
              panNo: identity.panNumber || "",
              panName: identity.panName || "",
              aadharFrontDocument: findDocs('Aadhar Front'),
              aadharBackDocument: findDocs('Aadhar Back'),
              panDocument: findDocs('PAN'),
              additionalIdType: identity.additionalIdType || "",
              additionalIdNumber: identity.additionalIdNumber || "",
              additionalIdDocument: findDocs(identity.additionalIdType || 'Additional ID'),
              nominees: (fetchedData.nominees && fetchedData.nominees.filter(n => n.nomineeType !== 'ESIC_Dependent').length > 0) ? fetchedData.nominees.filter(n => n.nomineeType !== 'ESIC_Dependent').map(n => ({
                id: n.id || null,
                nomineeName: n.nomineeName || "",
                contactNo: n.contactNo || "",
                nomineeDob: n.nomineeDob ? n.nomineeDob.split('T')[0] : "",
                relation: n.relation || "",
                aadhaarNumber: n.aadhaarNumber || "",
                aadhaarFrontPath: n.aadhaarFrontPath || "",
                aadhaarBackPath: n.aadhaarBackPath || "",
                sharePercentage: n.sharePercentage !== undefined ? n.sharePercentage : 100.00,
                nomineeType: n.nomineeType || "Normal"
              })) : [
                { id: null, nomineeName: "", contactNo: "", nomineeDob: "", relation: "", sharePercentage: 100.00, nomineeType: "Normal" }
              ],
              dependents: (fetchedData.nominees && fetchedData.nominees.filter(n => n.nomineeType === 'ESIC_Dependent').length > 0) ? fetchedData.nominees.filter(n => n.nomineeType === 'ESIC_Dependent').map(n => ({
                id: n.id || null,
                nomineeName: n.nomineeName || "",
                contactNo: n.contactNo || "",
                nomineeDob: n.nomineeDob ? n.nomineeDob.split('T')[0] : "",
                relation: n.relation || "",
                aadhaarNumber: n.aadhaarNumber || "",
                aadhaarFrontPath: n.aadhaarFrontPath || "",
                aadhaarBackPath: n.aadhaarBackPath || "",
                sharePercentage: 100.00,
                nomineeType: 'ESIC_Dependent'
              })) : [
                { id: null, nomineeName: "", contactNo: "", nomineeDob: "", relation: "", sharePercentage: 100.00, nomineeType: "ESIC_Dependent" }
              ]
            }));
          }

          const joiningDateVal = fetchedData.expectedJoiningDate || fetchedData.dateOfJoining;
          if (joiningDateVal) {
            const joiningDate = new Date(joiningDateVal).toISOString().split('T')[0];
            setJoiningFormsDraft(prev => ({ ...prev, expectedJoiningDate: joiningDate }));
          }

          if (fetchedData.education?.length > 0) {
            setEducationList(fetchedData.education.map(e => ({
              qualificationLevel: e.qualificationLevel || "",
              degree: e.degree || "",
              branch: e.branch || "",
              collegeName: e.collegeName || "",
              affiliatedUniversity: e.affiliatedUniversity || "",
              yearOfJoining: e.yearOfJoining || "",
              yearOfCompletion: e.yearOfCompletion || "",
              cgpaPercentage: e.cgpaPercentage || "",
              state: e.state || "",
              attachments: findDocs('Education Document', e.id),
            })));
          }

          if (fetchedData.bankDetails) {
            const bank = fetchedData.bankDetails;
            setBankDraft(prev => ({
              ...prev,
              accountName: bank.accountName || "",
              accountNumber: bank.accountNumber || "",
              bankName: bank.bankName || "",
              ifscCode: bank.ifscCode || "",
              branchName: bank.branchName || "",
              passbookDocument: findDocs('Bank Passbook/Cancelled Check'),
            }));
          }

          if (fetchedData.experience?.length > 0) {
            setEmploymentList(fetchedData.experience.map(exp => ({
              companyName: exp.companyName || "",
              department: exp.department || "",
              role: exp.designation || exp.role || "",
              ctc: exp.ctc || "",
              fromDate: exp.dateOfJoining ? exp.dateOfJoining.split('T')[0] : "",
              toDate: exp.dateOfRelieving ? exp.dateOfRelieving.split('T')[0] : "",
              address: exp.address || "",
              offerLetter: findDocs('offerLetter', exp.id),
              relievingLetter: findDocs('relievingLetter', exp.id),
              experienceLetter: findDocs('experienceLetter', exp.id),
              payslips: findDocs('payslips', exp.id),
              incrementLetter: findDocs('incrementLetter', exp.id),
            })));
          }

          // Initialize dynamic joining forms from requirements
          const requirements = fetchedData.documentRequirements?.requiredDocuments || [];
          const initialJoiningForms = {};
          requirements.forEach(req => {
            const formKey = req.templateName || req.label;
            initialJoiningForms[formKey] = findDocs(formKey);
          });

          // Initialize Form 12BB and Declaration of Non-PF if they exist
          initialJoiningForms['Form 12BB'] = findDocs('Form 12BB');
          initialJoiningForms['Declaration of Non-PF'] = findDocs('Declaration of Non-PF');
          initialJoiningForms['ESIC Form'] = findDocs('ESIC Form');
          initialJoiningForms['Signed Offer Letter'] = findDocs('Signed Offer Letter');

          // Initialize taxDeclaration state based on HR's collect12BB preference
          const collect12BB = fetchedData.compensation?.collect12BB;
          const has12BB = collect12BB === true || collect12BB === 'true' || collect12BB === 'yes';
          initialJoiningForms['taxDeclaration'] = has12BB ? 'Yes' : 'No';

          setJoiningFormsDraft(prev => ({
            ...prev,
            ...initialJoiningForms
          }));

          setTokenError(null);
        } else {
          setIsTokenValid(false);
          setTokenError(response.data.message || 'Invalid token');
        }
      } catch (error) {
        console.error('Token verification error:', error);
        setIsTokenValid(false);
        setTokenError(error.response?.data?.error || error.response?.data?.message || 'Error verifying token. Please try again.');
      } finally {
        setIsVerifying(false);
      }

    };

    verifyToken();
  }, [searchParams, showError]);

  const handleFileUpload = useCallback((filesInput, category, index = null, documentType = null) => {
    const files = filesInput.target ? Array.from(filesInput.target.files) : filesInput;
    if (!files || !Array.isArray(files) || files.length === 0) return;

    if (category === 'education' && index !== null) {
      setEducationList(prev =>
        prev.map((item, i) =>
          i === index
            ? { ...item, attachments: [...item.attachments, ...files] }
            : item
        )
      );
    } else if (category === 'employment' && index !== null && documentType) {
      setEmploymentList(prev =>
        prev.map((item, i) =>
          i === index
            ? { ...item, [documentType]: [...(item[documentType] || []), ...files] }
            : item
        )
      );
    } else if (category === 'joiningForms') {
      setJoiningFormsDraft(prev => ({
        ...prev,
        [documentType]: [...(prev[documentType] || []), ...files]
      }));
    } else if (category === 'personal') {
      if (documentType === 'rentalAgreement') {
        setPersonalDraft(prev => ({
          ...prev,
          presentAddress: {
            ...prev.presentAddress,
            rentalAgreement: files
          }
        }));
      } else if (documentType === 'disabilityCertificate') {
        setPersonalDraft(prev => ({
          ...prev,
          disabilityCertificate: files
        }));
      } else if (documentType === 'passportPhoto') {
        setPersonalDraft(prev => ({
          ...prev,
          passportPhoto: files
        }));
      } else if (documentType) {
        setPersonalDraft(prev => ({
          ...prev,
          [`${documentType}Document`]: files
        }));
      }
    }
  }, []);

  const removeFile = useCallback((category, index, fileIndex, documentType = null) => {
    if (category === 'education') {
      setEducationList(prev =>
        prev.map((item, i) =>
          i === index
            ? { ...item, attachments: item.attachments.filter((_, j) => j !== fileIndex) }
            : item
        )
      );
    } else if (category === 'employment' && index !== null && documentType) {
      setEmploymentList(prev =>
        prev.map((item, i) =>
          i === index
            ? { ...item, [documentType]: item[documentType].filter((_, j) => j !== fileIndex) }
            : item
        )
      );
    } else if (category === 'joiningForms') {
      setJoiningFormsDraft(prev => ({
        ...prev,
        [documentType]: (prev[documentType] || []).filter((_, j) => j !== fileIndex)
      }));
    } else if (category === 'personal') {
      if (documentType === 'rentalAgreement') {
        setPersonalDraft(prev => ({
          ...prev,
          presentAddress: {
            ...prev.presentAddress,
            rentalAgreement: (prev.presentAddress?.rentalAgreement || []).filter((_, j) => j !== fileIndex)
          }
        }));
      } else if (documentType === 'disabilityCertificate') {
        setPersonalDraft(prev => ({
          ...prev,
          disabilityCertificate: (prev.disabilityCertificate || []).filter((_, j) => j !== fileIndex)
        }));
      } else if (documentType === 'passportPhoto') {
        setPersonalDraft(prev => ({
          ...prev,
          passportPhoto: (prev.passportPhoto || []).filter((_, j) => j !== fileIndex)
        }));
      } else if (documentType) {
        setPersonalDraft(prev => ({
          ...prev,
          [`${documentType}Document`]: (prev[`${documentType}Document`] || []).filter((_, j) => j !== fileIndex)
        }));
      }
    }
  }, []);

  const savePersonalInformation = async () => {
    try {
      // Safety check for personalDraft
      if (!personalDraft) {
        throw new Error('Personal information data is missing. Please refresh the page.');
      }

      const formData = new FormData();
      const { aadharFrontDocument, aadharBackDocument, panDocument, additionalIdDocument, disabilityCertificate, passportPhoto, nominees = [], dependents = [], ...dataToSend } = personalDraft;

      const collectESIC = employeeData?.compensation?.collectESIC;
      const hasESIC = collectESIC === true || collectESIC === 'true' || collectESIC === 'yes';

      const combinedNominees = hasESIC ? [...nominees, ...dependents] : nominees;

      const sanitizedNominees = combinedNominees
        .filter(n => n.nomineeName && n.nomineeName.trim() !== '')
        .map(n => ({
          ...n,
          nomineeDob: n.nomineeDob || null,
          aadhaarFrontFile: undefined,
          aadhaarBackFile: undefined
        }));

      const addressData = {
        ...dataToSend,
        presentAddress: { ...(dataToSend.presentAddress || {}), rentalAgreement: undefined },
        nominees: sanitizedNominees
      };
      formData.append('data', JSON.stringify(addressData));

      // Append files
      if (personalDraft) {
        if (personalDraft.aadharFrontDocument) {
          personalDraft.aadharFrontDocument.forEach((file) => {
            if (file instanceof File) formData.append('aadharFrontDocument', file);
          });
        }
        if (personalDraft.aadharBackDocument) {
          personalDraft.aadharBackDocument.forEach((file) => {
            if (file instanceof File) formData.append('aadharBackDocument', file);
          });
        }
        if (personalDraft.panDocument) {
          personalDraft.panDocument.forEach((file) => {
            if (file instanceof File) formData.append('panDocument', file);
          });
        }
        if (personalDraft.disabilityCertificate) {
          personalDraft.disabilityCertificate.forEach((file) => {
            if (file instanceof File) formData.append('disabilityCertificate', file);
          });
        }
        if (personalDraft.additionalIdDocument) {
          personalDraft.additionalIdDocument.forEach((file) => {
            if (file instanceof File) formData.append('additionalIdDocument', file);
          });
        }
        if (personalDraft.presentAddress?.rentalAgreement) {
          personalDraft.presentAddress.rentalAgreement.forEach((file) => {
            if (file instanceof File) formData.append('rentalAgreement', file);
          });
        } if (personalDraft.passportPhoto) {
          personalDraft.passportPhoto.forEach((file) => {
            if (file instanceof File) formData.append('passportPhoto', file);
          });
        }

        // Nominee Aadhaar files
        combinedNominees.forEach((n, index) => {
          if (n.aadhaarFrontFile) {
            formData.append(`nominee_${index}_aadhaarFront`, n.aadhaarFrontFile);
          }
          if (n.aadhaarBackFile) {
            formData.append(`nominee_${index}_aadhaarBack`, n.aadhaarBackFile);
          }
        });
      }

      const response = await apiClient.post(
        `/onboarding/portal/personal-info`,
        formData,
        {
          headers: {
            'x-onboarding-token': token,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error saving personal information:', error);
      throw error;
    }
  };

  const saveBankDetails = async () => {
    try {
      const formData = new FormData();
      formData.append('data', JSON.stringify(bankDraft));
      if (bankDraft.passbookDocument && bankDraft.passbookDocument.length > 0) {
        bankDraft.passbookDocument.forEach(file => {
          if (file instanceof File) {
            formData.append('passbookDocument', file);
          }
        });
      }

      const response = await apiClient.post(
        `/onboarding/portal/bank-details`,
        formData,
        {
          headers: {
            'x-onboarding-token': token,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error saving bank details:', error);
      throw error;
    }
  };

  const saveEducationDetails = async () => {
    try {
      const formData = new FormData();
      formData.append('education', JSON.stringify(educationList));
      educationList.forEach((edu, index) => {
        if (edu.attachments && Array.isArray(edu.attachments)) {
          edu.attachments.forEach((file) => {
            if (file instanceof File) formData.append(`educationDocument_${index}`, file);
          });
        }
      });

      const response = await apiClient.post(
        `/onboarding/portal/education`,
        formData,
        {
          headers: {
            'x-onboarding-token': token,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error saving education details:', error);
      throw error;
    }
  };

  const saveEmploymentDetails = async () => {
    // Filter out empty employment entries
    const validEmployments = employmentList.filter(emp =>
      emp.companyName?.trim() || emp.role?.trim()
    );

    if (validEmployments.length === 0) {
      return { success: true, message: "No employment data to save" };
    }

    try {
      const formData = new FormData();
      formData.append('experience', JSON.stringify(validEmployments));
      const docTypes = ['relievingLetter', 'experienceLetter', 'payslips'];
      validEmployments.forEach((emp, index) => {
        docTypes.forEach(type => {
          if (emp[type] && Array.isArray(emp[type])) {
            emp[type].forEach((file) => {
              if (file instanceof File) formData.append(`experienceDocument_${index}_${type}`, file);
            });
          }
        });
      });

      const response = await apiClient.post(
        `/onboarding/portal/experience`,
        formData,
        {
          headers: {
            'x-onboarding-token': token,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error saving employment details:', error);
      throw error;
    }
  };

  const saveJoiningForms = async () => {
    try {
      const formData = new FormData();
      Object.keys(joiningFormsDraft).forEach(formName => {
        if (formName !== 'nominees' && Array.isArray(joiningFormsDraft[formName]) && joiningFormsDraft[formName].length > 0) {
          joiningFormsDraft[formName].forEach(file => {
            if (file instanceof File) {
              formData.append('joiningForms', file);
              formData.append('formNames', formName);
            }
          });
        }
      });

      if (joiningFormsDraft?.expectedJoiningDate) {
        formData.append('expectedJoiningDate', joiningFormsDraft.expectedJoiningDate);
      }

      if (joiningFormsDraft?.nominees && Array.isArray(joiningFormsDraft.nominees)) {
        const sanitizedNominees = joiningFormsDraft.nominees.map(n => ({
          ...n,
          nomineeDob: n.nomineeDob || null,
          aadhaarFrontFile: undefined,
          aadhaarBackFile: undefined,
          photoFile: undefined
        }));
        formData.append('nomineeData', JSON.stringify(sanitizedNominees));

        joiningFormsDraft.nominees.forEach((n, index) => {
          if (n.aadhaarFrontFile) {
            formData.append(`nominee_${index}_aadhaarFront`, n.aadhaarFrontFile);
          }
          if (n.aadhaarBackFile) {
            formData.append(`nominee_${index}_aadhaarBack`, n.aadhaarBackFile);
          }
          if (n.photoFile) {
            formData.append(`nominee_${index}_photo`, n.photoFile);
          }
        });
      }

      const response = await apiClient.post(
        `/onboarding/portal/joining-forms`,
        formData,
        {
          headers: {
            'x-onboarding-token': token,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error saving joining forms:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    setSubmitMessage(null);
    setErrors({});
    let hasError = false;

    const checkRejectedDoc = (docArray, docType, errorMessage, index = null) => {
      if (!docArray || docArray.length === 0) return errorMessage;
      const isRejected = rejectedDocs.some(r =>
        (r.documentType === docType || r.documentType === `Joining Form: ${docType}` || r.documentType === `Experience: ${docType}` || r.documentType.startsWith(`Experience: ${docType}`)) &&
        (index === null || r.index === index)
      );
      if (isRejected && docArray.every(f => f.fromServer)) {
        return `Please upload a new ${docType} (Previous one was rejected)`;
      }
      return null;
    };

    try {
      if (activeTab === 1) {
        // Personal Information Validation
        /** @type {Record<string, string>} */
        const newErrors = {};

        // Safety check for personalDraft
        if (!personalDraft) {
          setErrors({ general: 'Personal information data is missing. Please refresh the page.' });
          return;
        }

        const {
          firstName, lastName, fatherName, motherName, personalEmail, alternativePhone,
          gender, dob, nationality, religion, bloodGroup, maritalStatus,
          aadharNo, panNo, emergencyContact, presentAddress, permanentAddress
        } = personalDraft || {};

        if (!firstName) newErrors.firstName = "First name is required";
        if (!lastName) newErrors.lastName = "Last name is required";
        if (!fatherName) newErrors.fatherName = "Father name is required";
        if (!motherName) newErrors.motherName = "Mother name is required";
        if (!personalEmail) {
          newErrors.personalEmail = "Personal email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalEmail)) {
          newErrors.personalEmail = "Please enter a valid email address";
        }
        if (!alternativePhone) {
          newErrors.alternativePhone = "Alternative phone is required";
        } else if (!/^\d{10}$/.test(alternativePhone)) {
          newErrors.alternativePhone = "Please enter a valid 10-digit alternative phone number";
        }

        if (!gender) newErrors.gender = "Gender is required";

        if (!dob) {
          newErrors.dob = "Date of Birth is required";
        } else {
          const birthDate = new Date(dob);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const m = today.getMonth() - birthDate.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          if (age < 18) newErrors.dob = "Employee must be at least 18 years old";
        }

        if (!nationality) newErrors.nationality = "Nationality is required";
        if (!religion) newErrors.religion = "Religion is required";
        if (!bloodGroup) newErrors.bloodGroup = "Blood Group is required";
        if (!maritalStatus) newErrors.maritalStatus = "Marital Status is required";

        // Spouse Name Validation (if married)
        if (maritalStatus === 'Married' && !personalDraft.spouseName) {
          newErrors.spouseName = "Spouse name is required when married";
        }

        if (!personalDraft.physicallyChallenged) newErrors.physicallyChallenged = "Physically challenged status is required";

        if (personalDraft.physicallyChallenged === 'Yes') {
          if (!personalDraft.disabilityDetails?.trim()) {
            newErrors.disabilityDetails = "Disability details are required";
          }
          const disabilityErr = checkRejectedDoc(personalDraft.disabilityCertificate, 'Disability Certificate', "Disability certificate is required");
          if (disabilityErr) newErrors.disabilityCertificate = disabilityErr;
        }

        if (!aadharNo) {
          newErrors.aadharNo = "Aadhar Number is required";
        } else if (!/^\d{12}$/.test(aadharNo)) {
          newErrors.aadharNo = "Aadhar Number must be exactly 12 digits";
        }
        if (!personalDraft.aadharName?.trim()) {
          newErrors.aadharName = "Name as per Aadhar is required";
        }

        const aadharFrontErr = checkRejectedDoc(personalDraft.aadharFrontDocument, 'Aadhar Front', "Please upload Aadhaar Front side");
        if (aadharFrontErr) newErrors.aadharFrontDocument = aadharFrontErr;

        const aadharBackErr = checkRejectedDoc(personalDraft.aadharBackDocument, 'Aadhar Back', "Please upload Aadhaar Back side");
        if (aadharBackErr) newErrors.aadharBackDocument = aadharBackErr;

        if (!panNo) {
          newErrors.panNo = "PAN Number is required";
        } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(panNo)) {
          newErrors.panNo = "Please enter a valid PAN Number (e.g. ABCDE1234F)";
        }
        if (!personalDraft.panName?.trim()) {
          newErrors.panName = "Name as per PAN is required";
        }

        const panErr = checkRejectedDoc(personalDraft.panDocument, 'PAN', "Please upload a copy of your PAN Card");
        if (panErr) newErrors.panDocument = panErr;

        // Additional ID validation
        if (personalDraft.additionalIdType) {
          if (!personalDraft.additionalIdNumber?.trim()) {
            newErrors.additionalIdNumber = `${personalDraft.additionalIdType} number is required`;
          }
          const addIdErr = checkRejectedDoc(personalDraft.additionalIdDocument, personalDraft.additionalIdType, `Please upload a copy of your ${personalDraft.additionalIdType}`);
          if (addIdErr) newErrors.additionalIdDocument = addIdErr;
        }

        if (!emergencyContact?.relation) newErrors.emergencyRelation = "Relation is required";
        if (!emergencyContact?.name) newErrors.emergencyName = "Name is required";
        if (!emergencyContact?.phone) {
          newErrors.emergencyPhone = "Phone is required";
        } else if (!/^\d{10}$/.test(emergencyContact.phone)) {
          newErrors.emergencyPhone = "Emergency contact phone number must be 10 digits";
        }

        // Present Address Validation
        if (!presentAddress?.accommodationType) newErrors.accommodationType = "Preferred Accommodation Type is required";

        const requiredAddrFields = ['houseNo', 'floor', 'houseName', 'street', 'landmark', 'city', 'state', 'pincode'];
        requiredAddrFields.forEach(field => {
          if (!presentAddress?.[field] || !String(presentAddress[field]).trim()) newErrors[`present_${field}`] = `Present address ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`;
        });

        requiredAddrFields.forEach(field => {
          if (!permanentAddress?.[field] || !String(permanentAddress[field]).trim()) newErrors[`permanent_${field}`] = `Permanent address ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`;
        });

        // Nominee Validation (Always mandatory, at least 1 nominee required)
        if (!personalDraft.nominees || personalDraft.nominees.length === 0) {
          newErrors.nominees = 'At least one nominee is required';
        } else {
          let totalShare = 0;
          personalDraft.nominees.forEach((nominee, idx) => {
            if (!nominee.nomineeName?.trim()) newErrors[`nomineeName_${idx}`] = "Nominee name is required";
            if (!nominee.relation?.trim()) newErrors[`nomineeRelation_${idx}`] = "Relation is required";
            if (!nominee.contactNo?.trim()) newErrors[`nomineeContact_${idx}`] = "Contact number is required";
            if (!nominee.nomineeDob) {
              newErrors[`nomineeDob_${idx}`] = "Date of birth is required";
            } else if (new Date(nominee.nomineeDob) > new Date()) {
              newErrors[`nomineeDob_${idx}`] = "DOB cannot be in the future";
            }

            if (!nominee.aadhaarNumber?.trim()) {
              newErrors[`nomineeAadhaarNumber_${idx}`] = "Aadhaar number is required";
            } else if (!/^\d{12}$/.test(nominee.aadhaarNumber)) {
              newErrors[`nomineeAadhaarNumber_${idx}`] = "Aadhaar number must be exactly 12 digits";
            }

            if (!nominee.aadhaarFrontFile && !nominee.aadhaarFrontPath) {
              newErrors[`nomineeAadhaarFront_${idx}`] = "Aadhaar front copy is required";
            }

            if (!nominee.aadhaarBackFile && !nominee.aadhaarBackPath) {
              newErrors[`nomineeAadhaarBack_${idx}`] = "Aadhaar back copy is required";
            }

            const share = parseFloat(nominee.sharePercentage);
            totalShare += isNaN(share) ? 0 : share;
          });
          if (personalDraft.nominees.length > 0 && Math.abs(totalShare - 100) > 0.01) {
            newErrors.nominees = `Total share percentage of all nominees must be exactly 100% (currently ${totalShare}%)`;
          }
        }




        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors);
          const validationSummary = getPersonalInfoValidationSummary(newErrors);
          setSubmitMessage({
            type: 'error',
            text: validationSummary || "Please check the form for errors highlighted in red."
          });
          window.scrollTo(0, 0);
          return;
        }

        setIsSubmitting(true);
        await savePersonalInformation();
        setProgress(prev => ({ ...prev, personalInfoCompleted: true }));
        const nextRejections = getFilteredRejections(1);
        setRejectedDocs(nextRejections);
        setActiveTab(getNextTab(1, nextRejections));
        setSubmitMessage({ type: 'success', text: 'Personal information saved!' });
      } else if (activeTab === 2) {
        // Bank details validation and save
        const bankErrors = validateBankDetails(bankDraft);
        const passbookErr = checkRejectedDoc(bankDraft.passbookDocument, 'Bank Passbook/Cancelled Check', "Please upload passbook or cancelled cheque");
        if (passbookErr) bankErrors.passbookDocument = passbookErr;

        if (hasValidationErrors(bankErrors)) {
          setErrors(bankErrors);
          setSubmitMessage({
            type: 'error',
            text: "Please complete all required bank details highlighted in red."
          });
          window.scrollTo(0, 0);
          return;
        }

        setIsSubmitting(true);
        await saveBankDetails();
        setProgress(prev => ({ ...prev, bankDetailsCompleted: true }));
        const nextRejections = getFilteredRejections(2);
        setRejectedDocs(nextRejections);
        setActiveTab(getNextTab(2, nextRejections));
        setSubmitMessage({ type: 'success', text: 'Bank details saved!' });
      } else if (activeTab === 3) {
        // Education details validation and save
        const educationErrors = validateEducationDetails(educationList);
        educationList.forEach((e, i) => {
          const err = checkRejectedDoc(e.attachments, 'Education Document', null, i);
          if (err) {
            educationErrors[i] = educationErrors[i] || {};
            educationErrors[i].attachments = err;
          }
        });

        if (hasValidationErrors(educationErrors)) {
          setErrors(educationErrors);
          setSubmitMessage({
            type: 'error',
            text: educationErrors._form || "Please complete all required education details highlighted in red."
          });
          window.scrollTo(0, 0);
          return;
        }

        setIsSubmitting(true);
        await saveEducationDetails();
        setProgress(prev => ({ ...prev, educationCompleted: true }));
        const nextRejections = getFilteredRejections(3);
        setRejectedDocs(nextRejections);
        setActiveTab(getNextTab(3, nextRejections));
        setSubmitMessage({ type: 'success', text: 'Education details saved!' });

      } else if (activeTab === 4) {
        // Employment details validation and save
        const employmentErrors = validateEmploymentDetails(employmentList, employeeData?.isFresher);
        employmentList.forEach((e, i) => {
          const docs = ['offerLetter', 'relievingLetter', 'experienceLetter', 'payslips', 'incrementLetter'];
          docs.forEach(doc => {
            const err = checkRejectedDoc(e[doc], 'Experience Documents', null, i);
            if (err) {
              employmentErrors[i] = employmentErrors[i] || {};
              employmentErrors[i][doc] = `Please upload a new document (Previous experience documents were rejected)`;
            }
          });
        });

        if (hasValidationErrors(employmentErrors)) {
          setErrors(employmentErrors);
          setSubmitMessage({
            type: 'error',
            text: "Please complete all required employment details highlighted in red."
          });
          window.scrollTo(0, 0);
          return;
        }

        setIsSubmitting(true);
        await saveEmploymentDetails();
        setProgress(prev => ({ ...prev, employmentCompleted: true }));
        const nextRejections = getFilteredRejections(4);
        setRejectedDocs(nextRejections);
        setActiveTab(getNextTab(4, nextRejections));
        setSubmitMessage({ type: 'success', text: 'Employment details saved!' });
      } else if (activeTab === 5) {
        // Joining forms validation and save
        const requirements = employeeData?.documentRequirements?.requiredDocuments || [];
        const joiningFormsErrors = {};
        const hasExistingPF = employeeData?.hasExistingPF ?? employeeData?.compensation?.hasExistingPF;
        const hasPF = hasExistingPF === true || hasExistingPF === 'true' || hasExistingPF === 'yes';

        requirements.forEach(req => {
          const formName = req.template?.templateName || req.templateName || req.label;
          if (!formName) return;

          // Skip Form 11 and PF Form 2 if hasPF is false
          if (!hasPF && (formName === 'form11' || formName === 'pfForm2')) return;

          const err = checkRejectedDoc(joiningFormsDraft[formName], formName, null);
          if (err) {
            joiningFormsErrors[formName] = `Please upload a new document (Previous one was rejected)`;
          } else if (req.isMandatory && (!joiningFormsDraft[formName] || joiningFormsDraft[formName].length === 0) && !progress.onboardingFormsUploaded?.includes(formName)) {
            joiningFormsErrors[formName] = 'Please upload this document';
          }
        });


        // Expected Joining Date Validation
        if (!joiningFormsDraft.expectedJoiningDate) {
          joiningFormsErrors.expectedJoiningDate = 'Please select your expected joining date';
        } else {
          const selectedDate = new Date(joiningFormsDraft.expectedJoiningDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (selectedDate < today) {
            joiningFormsErrors.expectedJoiningDate = 'Expected joining date cannot be in the past';
          }
        }

        // ESIC Nominee Validation if required by templates
        const reqList = employeeData?.onboardingRequirements || employeeData?.documentRequirements?.requiredDocuments || [];
        const needsNominees = reqList.some(req =>
          req.isNomineeRequired || req.template?.isNomineeRequired
        );
        if (needsNominees) {
          const nominees = joiningFormsDraft.nominees || [];
          if (nominees.length === 0) {
            joiningFormsErrors.nominees = 'At least one nominee is required';
          } else {
            nominees.forEach((nominee, idx) => {
              if (!nominee.nomineeName?.trim()) joiningFormsErrors[`nomineeName_${idx}`] = "Nominee name is required";
              if (!nominee.relation?.trim()) joiningFormsErrors[`nomineeRelation_${idx}`] = "Relation is required";
              if (!nominee.contactNo?.trim()) joiningFormsErrors[`nomineeContact_${idx}`] = "Contact number is required";
              if (!nominee.nomineeDob) {
                joiningFormsErrors[`nomineeDob_${idx}`] = "Date of birth is required";
              } else if (new Date(nominee.nomineeDob) > new Date()) {
                joiningFormsErrors[`nomineeDob_${idx}`] = "DOB cannot be in the future";
              }

              if (!nominee.aadhaarNumber?.trim()) {
                joiningFormsErrors[`nomineeAadhaarNumber_${idx}`] = "Aadhaar number is required";
              } else if (!/^\d{12}$/.test(nominee.aadhaarNumber)) {
                joiningFormsErrors[`nomineeAadhaarNumber_${idx}`] = "Aadhaar number must be exactly 12 digits";
              }

              if (!nominee.aadhaarFrontFile && !nominee.aadhaarFrontPath) {
                joiningFormsErrors[`nomineeAadhaarFront_${idx}`] = "Aadhaar front copy is required";
              }

              if (!nominee.aadhaarBackFile && !nominee.aadhaarBackPath) {
                joiningFormsErrors[`nomineeAadhaarBack_${idx}`] = "Aadhaar back copy is required";
              }
            });
          }
        }

        // Passport Photo Validation
        const isPassportPhotoUploaded = progress.onboardingFormsUploaded?.includes('passportPhoto');
        const hasPassportPhotoDraft = joiningFormsDraft.passportPhoto && joiningFormsDraft.passportPhoto.length > 0;
        if (!isPassportPhotoUploaded && !hasPassportPhotoDraft) {
          joiningFormsErrors.passportPhoto = 'Please upload your passport size photograph';
        }

        // Tax Declaration (Form 12BB) Validation
        const collect12BB = employeeData?.compensation?.collect12BB;
        const has12BB = collect12BB === true || collect12BB === 'true' || collect12BB === 'yes';
        if (has12BB) {
          const formName = 'Form 12BB';
          const isUploaded = progress.onboardingFormsUploaded?.includes(formName);
          const hasDraftFile = joiningFormsDraft[formName] && joiningFormsDraft[formName].length > 0;
          if (!isUploaded && !hasDraftFile) {
            joiningFormsErrors[formName] = 'Please upload this document';
          }
        }

        // ESIC Form Validation
        const collectESIC = employeeData?.compensation?.collectESIC;
        const hasESIC = collectESIC === true || collectESIC === 'true' || collectESIC === 'yes';
        if (hasESIC) {
          const formName = 'ESIC Form';
          const isUploaded = progress.onboardingFormsUploaded?.includes(formName);
          const hasDraftFile = joiningFormsDraft[formName] && joiningFormsDraft[formName].length > 0;
          if (!isUploaded && !hasDraftFile) {
            joiningFormsErrors[formName] = 'Please upload this document';
          }
        }

        // Declaration of Non-PF Validation
        if (hasPF) {
          const formName = 'Declaration of Non-PF';
          const isUploaded = progress.onboardingFormsUploaded?.includes(formName);
          const hasDraftFile = joiningFormsDraft[formName] && joiningFormsDraft[formName].length > 0;
          if (!isUploaded && !hasDraftFile) {
            joiningFormsErrors[formName] = 'Please upload this document';
          }
        }

        // Signed Offer Letter Validation
        {
          const formName = 'Signed Offer Letter';
          const isUploaded = progress.onboardingFormsUploaded?.includes(formName);
          const hasDraftFile = joiningFormsDraft[formName] && joiningFormsDraft[formName].length > 0;
          if (!isUploaded && !hasDraftFile) {
            joiningFormsErrors[formName] = 'Please upload this document';
          }
        }

        if (Object.keys(joiningFormsErrors).length > 0) {
          setErrors(joiningFormsErrors);
          setSubmitMessage({
            type: 'error',
            text: "Please complete all required joining forms and nominee details highlighted in red."
          });
          window.scrollTo(0, 0);
          return;
        }

        // Clear any existing joining forms errors if validation passes
        setErrors(prev => {
          const nextErrors = { ...prev };
          // Remove joining forms errors if they exist
          Object.keys(joiningFormsDraft).forEach(formName => {
            delete nextErrors[formName];
          });
          return nextErrors;
        });

        setIsSubmitting(true);
        const result = await saveJoiningForms();
        if (result.success && result.data?.onboardingProgress) {
          setProgress(result.data.onboardingProgress);
        } else {
          setProgress(prev => ({ ...prev, joiningFormsCompleted: true, isFullyCompleted: true }));
        }
        const nextRejections = getFilteredRejections(5);
        setRejectedDocs(nextRejections);
        setActiveTab(0);
        setSubmitMessage({
          type: 'success',
          text: 'Onboarding submitted successfully! You will receive a confirmation email.'
        });
      }
    } catch (error) {
      hasError = true;
      // Show the detailed error modal for technical errors
      showError(error);

      setSubmitMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Failed to save details. Please try again.'
      });
      window.scrollTo(0, 0);
    } finally {
      setIsSubmitting(false);
      // Clear message after 3 seconds if not final submission
      if (!hasError && activeTab !== 3) {
        setTimeout(() => setSubmitMessage(null), 3000);
      }
    }
  };

  // Skip employment section for candidates explicitly marked as Freshers
  const shouldSkipEmployment = !!employeeData?.isFresher;

  const handleBack = useCallback(() => {
    setErrors({});
    setSubmitMessage(null);
    if (activeTab === 2) {
      setActiveTab(1);
    } else if (activeTab === 3) {
      setActiveTab(2);
    } else if (activeTab === 4) {
      setActiveTab(3);
    } else if (activeTab === 5) {
      if (shouldSkipEmployment) {
        setActiveTab(3);
      } else {
        setActiveTab(4);
      }
    }
    window.scrollTo(0, 0);
  }, [activeTab, shouldSkipEmployment]);

  // Step index calculation for the UI Stepper (0-indexed)
  const currentActiveStep = (activeTab <= 3) ? (activeTab - 1) : (shouldSkipEmployment ? activeTab - 2 : activeTab - 1);

  const renderTabContent = useCallback(() => {
    if (activeTab === 1) {
      return (
        <PersonalInformationSection
          personalDraft={personalDraft}
          onPersonalDraftChange={setPersonalDraft}
          validationErrors={errors}
          rejectedDocs={rejectedDocs}
          onFileUpload={handleFileUpload}
          onFileRemove={removeFile}
          onClearError={(key) => setErrors(prev => { const next = { ...prev }; delete next[key]; return next; })}
          requirements={employeeData?.documentRequirements?.requiredDocuments || []}
          employeeData={employeeData}
          isSubmitting={isSubmitting}
        />
      );
    } else if (activeTab === 2) {
      return (
        <BankDetailsSection
          bankDraft={bankDraft}
          onBankDraftChange={setBankDraft}
          validationErrors={errors}
          rejectedDocs={rejectedDocs}
          onFileUpload={handleFileUpload}
          onFileRemove={removeFile}
          onClearError={(key) => setErrors(prev => { const next = { ...prev }; delete next[key]; return next; })}
          isSubmitting={isSubmitting}
        />
      );
    } else if (activeTab === 3) {
      return (
        <EducationSection
          educationList={educationList}
          onEducationListChange={setEducationList}
          validationErrors={errors}
          rejectedDocs={rejectedDocs}
          educationLevel={employeeData?.educationLevel}
          onFileUpload={handleFileUpload}
          onFileRemove={removeFile}
          onClearError={(key) => setErrors(prev => { const next = { ...prev }; delete next[key]; return next; })}
          isSubmitting={isSubmitting}
        />
      );
    } else if (activeTab === 4) {
      if (!shouldSkipEmployment) {
        return (
          <EmploymentSection
            employmentList={employmentList}
            setEmploymentList={setEmploymentList}
            validationErrors={errors}
            rejectedDocs={rejectedDocs}
            onFileUpload={handleFileUpload}
            onFileRemove={removeFile}
            onClearError={(key) => setErrors(prev => { const next = { ...prev }; delete next[key]; return next; })}
            isFresher={employeeData?.isFresher}
            isSubmitting={isSubmitting}
          />
        );
      } else {
        // Skip employment section when shouldSkipEmployment is true
        return null;
      }
    } else if (activeTab === 5) {
      return (
        <JoiningFormsSection
          joiningFormsDraft={joiningFormsDraft}
          onJoiningFormsChange={(field, value) => setJoiningFormsDraft(prev => ({ ...prev, [field]: value }))}
          documentRequirements={employeeData?.documentRequirements?.requiredDocuments || []}
          onFileUpload={handleFileUpload}
          onFileRemove={removeFile}
          validationErrors={errors}
          rejectedDocs={rejectedDocs}
          uploadedForms={progress.onboardingFormsUploaded || []}
          requirements={employeeData?.onboardingRequirements || []}
          personalDraft={personalDraft}
          onClearError={(key) => setErrors(prev => { const next = { ...prev }; delete next[key]; return next; })}
          employeeData={employeeData}
          compensation={employeeData?.compensation}
          isSubmitting={isSubmitting}
        />
      );
    } else {
      return null;
    }
  }, [activeTab, personalDraft, bankDraft, educationList, employmentList, joiningFormsDraft, errors, rejectedDocs, handleFileUpload, removeFile, employeeData, progress.onboardingFormsUploaded, shouldSkipEmployment, isSubmitting]);

  const hasRejection = (tabNumber) => {
    if (!rejectedDocs || rejectedDocs.length === 0) return false;
    return rejectedDocs.some(doc => {
      const type = doc.documentType;
      if (tabNumber === 1) return ['Aadhar Front', 'Aadhar Back', 'PAN', 'Voter', 'Driving Licence', 'Passport', 'Additional ID'].includes(type);
      if (tabNumber === 2) return type === 'Bank Passbook/Cancelled Check';
      if (tabNumber === 3) return type === 'Education Document';
      if (tabNumber === 4) return type === 'Experience Documents';
      if (tabNumber === 5) return !['Aadhar Front', 'Aadhar Back', 'PAN', 'Voter', 'Driving Licence', 'Passport', 'Additional ID', 'Bank Passbook/Cancelled Check', 'Education Document', 'Experience Documents'].includes(type);
      return false;
    });
  };

  const getFilteredRejections = (tabNumber, currentRejectedDocs = rejectedDocs) => {
    if (!currentRejectedDocs) return [];
    return currentRejectedDocs.filter(doc => {
      const type = doc.documentType;
      if (tabNumber === 1) return !['Aadhar Front', 'Aadhar Back', 'PAN', 'Voter', 'Driving Licence', 'Passport', 'Additional ID'].includes(type);
      if (tabNumber === 2) return type !== 'Bank Passbook/Cancelled Check';
      if (tabNumber === 3) return type !== 'Education Document';
      if (tabNumber === 4) return type !== 'Experience Documents';
      if (tabNumber === 5) return ['Aadhar Front', 'Aadhar Back', 'PAN', 'Voter', 'Driving Licence', 'Passport', 'Additional ID', 'Bank Passbook/Cancelled Check', 'Education Document', 'Experience Documents'].includes(type);
      return true;
    });
  };

  const getNextTab = (currentTab, currentRejectedDocs = rejectedDocs) => {
    if (!progress.isFullyCompleted || !currentRejectedDocs || currentRejectedDocs.length === 0) {
      if (currentTab === 1) return 2;
      if (currentTab === 2) return 3;
      if (currentTab === 3) return shouldSkipEmployment ? 5 : 4;
      if (currentTab === 4) return 5;
      return 0;
    }

    const hasRejectionInList = (tabNumber) => {
      return currentRejectedDocs.some(doc => {
        const type = doc.documentType;
        if (tabNumber === 1) return ['Aadhar Front', 'Aadhar Back', 'PAN', 'Voter', 'Driving Licence', 'Passport', 'Additional ID'].includes(type);
        if (tabNumber === 2) return type === 'Bank Passbook/Cancelled Check';
        if (tabNumber === 3) return type === 'Education Document';
        if (tabNumber === 4) return type === 'Experience Documents';
        if (tabNumber === 5) return !['Aadhar Front', 'Aadhar Back', 'PAN', 'Voter', 'Driving Licence', 'Passport', 'Additional ID', 'Bank Passbook/Cancelled Check', 'Education Document', 'Experience Documents'].includes(type);
        return false;
      });
    };

    for (let nextTab = currentTab + 1; nextTab <= 5; nextTab++) {
      if (nextTab === 4 && shouldSkipEmployment) continue;
      if (hasRejectionInList(nextTab)) {
        return nextTab;
      }
    }
    return 0;
  };

  if (isVerifying) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", backgroundColor: theme.palette.background.default }}>
        <Stack spacing={3} alignItems="center">
          <CircularProgress size={60} />
          <Typography variant="h6" color="textSecondary">Verifying your onboarding link...</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: theme.palette.background.default }}>
      <AppBar position={isMobile ? "sticky" : "static"} elevation={0} sx={{ top: 0, zIndex: 1100, backgroundColor: theme.palette.common.white, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', minHeight: { xs: 64, md: 80 }, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: { xs: '100%', md: 'auto' }, mb: { xs: 1, md: 0 } }}>
            <img src={logo} alt="Company Logo" style={{ width: isMobile ? 35 : 45, height: isMobile ? 35 : 45, marginRight: 16 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main, fontSize: { xs: '1.5rem', md: '2rem' } }}>
              Employee Onboarding
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} width={{ xs: '100%', sm: 'auto' }}>
          </Stack>
        </Toolbar>
      </AppBar>

      {(saveMessage || submitMessage) && (
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          {saveMessage && (
            <Alert
              severity={saveMessage.type}
              sx={{ mb: 2 }}
              onClose={() => setSaveMessage(null)}
            >
              {saveMessage.text}
            </Alert>
          )}
          {submitMessage && (
            <Alert
              severity={submitMessage.type}
              sx={{ mb: 2 }}
              onClose={() => setSubmitMessage(null)}
            >
              {submitMessage.text}
            </Alert>
          )}
        </Container>
      )}

      {progress.isFullyCompleted && rejectedDocs.length === 0 ? (
        <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
          <Paper elevation={3} sx={{ p: 6, borderRadius: 4 }}>
            <Avatar sx={{ bgcolor: theme.palette.success.main, width: 80, height: 80, mx: 'auto', mb: 3 }}>
              <Description sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Onboarding Completed!
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Your onboarding information has been successfully submitted and verified.
              Our HR team will reach out to you if any further details are required.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 4 }}>
              If you have any questions, please contact the HR department.
            </Typography>
            <Button
              variant="outlined"
              sx={{ mt: 4, borderRadius: 2 }}
              onClick={() => window.location.href = 'mailto:hr@company.com'}
            >
              Contact Support
            </Button>
          </Paper>
        </Container>
      ) : (
        <Box sx={{ mt: { xs: 0, md: 4 }, pb: { xs: 10, md: 6 } }}>
          {!isTokenValid ? (
            <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
              <Paper elevation={4} sx={{ p: 6, borderRadius: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: theme.palette.error.main + '15', width: 90, height: 90, mb: 3 }}>
                  <LockClock sx={{ fontSize: 45, color: theme.palette.error.main }} />
                </Avatar>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: theme.palette.error.main, mb: 1 }}>
                  Link Expired
                </Typography>
                <Typography variant="h6" color="primary" paragraph sx={{ mb: 3, fontWeight: 600, maxWidth: '80%' }}>
                  {tokenError || 'Your onboarding link has expired or is invalid.'}
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                    px: 6,
                    py: 1.5,
                    backgroundColor: theme.palette.primary.main,
                    fontWeight: 600,
                    fontSize: '1rem',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: theme.palette.primary.dark, transform: 'translateY(-2px)' },
                    transition: 'all 0.2s'
                  }}
                  onClick={() => window.location.href = 'mailto:hr@company.com'}
                >
                  Contact HR Representative
                </Button>
              </Paper>
            </Container>
          ) : (
            <>
              <Box>
                <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 } }}>
                  {isMobile ? (
                    <Typography variant="h6" align="center" sx={{ fontWeight: 600, color: '#6F2DBD' }}>
                      {activeTab === 1 && 'Personal Information'}
                      {activeTab === 2 && 'Bank Details'}
                      {activeTab === 3 && 'Education Details'}
                      {activeTab === 4 && 'Employment Details'}
                      {activeTab === 5 && 'Joining Forms'}
                    </Typography>
                  ) : (
                    <Stepper
                      alternativeLabel
                      activeStep={currentActiveStep}
                      connector={
                        <StepConnector
                          sx={{
                            '& .MuiStepConnector-line': {
                              borderColor: '#E0E0E0',
                              borderTopWidth: 2,
                            },
                            '&.Mui-active .MuiStepConnector-line': {
                              borderColor: '#6F2DBD',
                            },
                            '&.Mui-completed .MuiStepConnector-line': {
                              borderColor: '#6F2DBD',
                            },
                          }}
                        />
                      }
                      sx={{
                        mb: 4,
                        '& .MuiStepIcon-root': {
                          color: '#E0E0E0',
                          '&.Mui-active': {
                            color: '#6F2DBD',
                          },
                          '&.Mui-completed': {
                            color: '#6F2DBD',
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'inherit',
                          },
                        },
                        '& .MuiStepIcon-root.Mui-completed': {
                          color: '#6F2DBD',
                        },
                        '& .MuiStepIcon-root.Mui-completed .MuiSvgIcon-root': {
                          color: '#6F2DBD',
                        },
                        '& .MuiStepIcon-text': {
                          fill: '#E0E0E0',
                        },
                        '& .MuiStepIcon-root.Mui-active .MuiStepIcon-text': {
                          fill: '#6F2DBD',
                        },
                        '& .MuiStepIcon-root.Mui-completed .MuiStepIcon-text': {
                          fill: '#6F2DBD',
                        },
                        '& .MuiStepLabel-label': {
                          '&.Mui-active': {
                            color: '#6F2DBD',
                            fontWeight: 600,
                          },
                          '&.Mui-completed': {
                            color: '#6F2DBD',
                            fontWeight: 600,
                          },
                        },
                      }}
                    >
                      <Step>
                        <StepLabel icon={<AccountCircle />} error={hasRejection(1)}>Personal Information</StepLabel>
                      </Step>
                      <Step>
                        <StepLabel icon={<AccountBalance />} error={hasRejection(2)}>Bank Details</StepLabel>
                      </Step>
                      <Step>
                        <StepLabel icon={<School />} error={hasRejection(3)}>Education Details</StepLabel>
                      </Step>
                      {!shouldSkipEmployment && (
                        <Step>
                          <StepLabel icon={<Work />} error={hasRejection(4)}>Employment Details</StepLabel>
                        </Step>
                      )}
                      <Step>
                        <StepLabel icon={<Description />} error={hasRejection(5)}>Joining Forms</StepLabel>
                      </Step>
                    </Stepper>
                  )}
                </Container>
              </Box>

              {rejectedDocs.length > 0 && (
                <Container maxWidth="xl">
                  <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                    <Typography variant="subtitle1" sx={{ color: '#EF4444', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      Action Required: Rejected Documents
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B91C1C', mb: 1, mt: 0.5 }}>
                      Some of your submitted documents were rejected. Please review the tabs below and re-upload them.
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 24, color: '#B91C1C', fontSize: '0.875rem' }}>
                      {rejectedDocs.map((doc, i) => (
                        <li key={i}>
                          <strong>{doc.documentType.replace('Joining Form: ', '').replace('Experience: ', '')}</strong>: {doc.rejectionReason}
                        </li>
                      ))}
                    </ul>
                  </Box>
                </Container>
              )}

              <Box sx={{ py: { xs: 2, sm: 4 } }}>
                {renderTabContent()}
              </Box>

              {(!progress.isFullyCompleted || rejectedDocs.length > 0) && (
                <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4 }}>
                  {isMobile ? (
                    <MobileStepper
                      variant="text"
                      steps={shouldSkipEmployment ? 4 : 5}
                      position="bottom"
                      activeStep={currentActiveStep}

                      sx={{ padding: 2, bgcolor: theme.palette.common.white, borderTop: `1px solid ${theme.palette.divider}` }}
                      nextButton={
                        <Button size="small" onClick={handleSubmit} disabled={isSaving || isSubmitting} variant="contained" sx={{ borderRadius: 2 }}>
                          {isSubmitting ? 'Saving...' : (activeTab === 5 ? 'Submit' : 'Next')}
                          {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
                        </Button>
                      }
                      backButton={
                        <Button size="small" onClick={handleBack} disabled={activeTab === 1 || isSaving || isSubmitting} variant="text" sx={{ borderRadius: 2 }}>
                          {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
                          Back
                        </Button>
                      }
                    />
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Button
                        variant="outlined"
                        onClick={handleBack}
                        disabled={activeTab === 1 || isSaving || isSubmitting}
                        startIcon={<KeyboardArrowLeft />}
                        sx={{
                          borderRadius: 2,
                          minWidth: '150px',
                          py: 1,
                          fontWeight: 600
                        }}
                      >
                        Back
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSaving || isSubmitting}
                        sx={{
                          borderRadius: 2,
                          backgroundColor: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                          },
                          minWidth: '150px',
                          py: 1,
                          fontWeight: 600
                        }}
                      >
                        {isSubmitting ? <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" /> : null}
                        {isSubmitting ? 'Saving...' : (activeTab === 5 ? 'Submit Onboarding' : 'Save & Next')}
                        {!isSubmitting && activeTab !== 5 && <KeyboardArrowRight sx={{ ml: 1 }} />}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      )}
      <ErrorModal />
    </Box>
  );
};

export default EmployeeOnboardingLanding;
